import { nanoid } from "nanoid";
import type {
  RoomEvent,
  VideoState,
  Participant,
  PlaylistItem,
  RoomPoll,
  VideoBookmark,
  SubtitleTrack,
  TimeComment,
  HistoryEntry,
  CustomReaction,
  ReactionIcon,
  RoomAnalytics,
} from "@/lib/room-state";
import { getAvatarColor } from "@/lib/room-state";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

export interface ChatMessage {
  id: number | string;
  userId: string;
  userName: string;
  content: string;
  type: "chat" | "system";
  createdAt: string;
}

export interface PreviewData {
  title: string | null;
  author: string | null;
  thumbnail: string | null;
  provider: string;
  url: string;
}

type InitData = {
  videoState: VideoState;
  participants: Participant[];
  queue: PlaylistItem[];
  bookmarks: VideoBookmark[];
  subtitles: SubtitleTrack | null;
  activePoll: RoomPoll | null;
  hostId: string;
  hostOnly: boolean;
  isLocked: boolean;
  title: string;
  raisedHands: Record<string, number>;
  comments: TimeComment[];
  history: HistoryEntry[];
  customReactions: CustomReaction[];
  analytics: RoomAnalytics | null;
  // admin_id from the DB record — the room creator's user_id. Used for admin
  // persistence: when a user rejoins and their userId === adminId, the
  // frontend restores their host/admin status. Optional because legacy rooms
  // (and local-mode rooms) don't have one.
  adminId?: string;
};

export class RoomStore {
  private roomId: string;
  private userId: string;
  private userName: string;
  private bc: BroadcastChannel | null = null;
  private channel: any | null = null;
  private listeners = new Set<(event: RoomEvent) => void>();
  private hasReceivedInit = false;
  private initTimeout: ReturnType<typeof setTimeout> | null = null;
  private stateProvider: (() => InitData | null) | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private stateSyncTimer: ReturnType<typeof setInterval> | null = null;
  // Debounced last_active_at flush — instead of updating the DB on every
  // send() (which would hammer Supabase), we flush at most once every 5s via
  // this timer. Set to a timestamp when there is pending activity to flush.
  private activitySyncTimer: ReturnType<typeof setInterval> | null = null;
  private pendingActivityFlush = false;
  // Client-side chat rate limiter — tracks message send timestamps per user
  // (in practice just this.userId, since send() is only called by the local
  // user). Used to enforce "max 10 messages per 10 seconds".
  private chatRateLog = new Map<string, number[]>();
  private static CHAT_RATE_LIMIT_WINDOW_MS = 10_000;
  private static CHAT_RATE_LIMIT_MAX = 10;

  constructor(roomId: string, userId: string, userName: string) {
    this.roomId = roomId;
    this.userId = userId;
    this.userName = userName;

    if (isSupabaseConfigured && supabase) {
      this.initSupabase();
    } else {
      this.initLocal();
    }
  }

  // ---- LOCAL MODE (BroadcastChannel) ----

  private initLocal() {
    this.bc = new BroadcastChannel(`syncscreen:room:${this.roomId}`);
    this.bc.onmessage = (e) => this.handleLocalMessage(e.data);

    this.bc.postMessage({ __type: "hello", userId: this.userId, userName: this.userName });

    this.initTimeout = setTimeout(() => {
      if (!this.hasReceivedInit) {
        this.hasReceivedInit = true;
        this.emit({
          type: "system",
          data: { message: `${this.userName} joined the watch party`, userId: this.userId, userName: this.userName },
          timestamp: Date.now(),
        });
      }
    }, 1500);

    this.heartbeatInterval = setInterval(() => {
      this.bc?.postMessage({
        __type: "heartbeat",
        userId: this.userId,
        userName: this.userName,
        timestamp: Date.now(),
      });
    }, 10000);
  }

  private handleLocalMessage(msg: any) {
    if (!msg || typeof msg !== "object") return;

    if (msg.__type === "hello") {
      if (this.hasReceivedInit && this.stateProvider) {
        const state = this.stateProvider();
        if (state) {
          this.bc?.postMessage({ type: "init", data: state, timestamp: Date.now() });
        }
      }
      this.emit({
        type: "system",
        data: { message: `${msg.userName} joined the watch party`, userId: msg.userId, userName: msg.userName },
        timestamp: Date.now(),
      });
      return;
    }

    if (msg.__type === "leave") {
      this.emit({
        type: "system",
        data: { message: `${msg.userName} left the room`, userId: msg.userId, userName: msg.userName },
        timestamp: Date.now(),
      });
      return;
    }

    if (msg.type === "init" && !this.hasReceivedInit) {
      this.hasReceivedInit = true;
      if (this.initTimeout) clearTimeout(this.initTimeout);
      this.emit(msg as RoomEvent);
      return;
    }

    this.emit(msg as RoomEvent);
  }

  // ---- SUPABASE MODE (Realtime channels + DB persistence) ----

  private async initSupabase() {
    if (!supabase) return;

    const channelName = `room:${this.roomId}`;
    const channel = supabase.channel(channelName, {
      config: {
        presence: { key: this.userId },
      },
    });

    this.channel = channel;

    // Listen for all broadcast events on this channel
    channel.on("broadcast", { event: "message" }, ({ payload }: { payload: RoomEvent }) => {
      const event = payload as RoomEvent;

      if (event.type === "__request_init") {
        if (this.hasReceivedInit && this.stateProvider) {
          const state = this.stateProvider();
          if (state) {
            channel.send({
              type: "broadcast",
              event: "message",
              payload: { type: "init", data: state, timestamp: Date.now() } as RoomEvent,
            });
          }
        }
        this.emit({
          type: "system",
          data: { message: `${event.data.userName} joined the watch party`, userId: event.data.userId, userName: event.data.userName },
          timestamp: Date.now(),
        });
        return;
      }

      if (event.type === "init") {
        this.hasReceivedInit = true;
        if (this.initTimeout) clearTimeout(this.initTimeout);
        this.emit(event);
        return;
      }

      if (event.type === "__leave") {
        this.emit({
          type: "system",
          data: { message: `${event.data.userName} left the room`, userId: event.data.userId, userName: event.data.userName },
          timestamp: Date.now(),
        });
        return;
      }

      // Ping/pong for latency measurement
      if (event.type === "__ping") {
        // Echo back a pong so the sender can measure round-trip
        this.channel?.send({
          type: "broadcast",
          event: "message",
          payload: {
            type: "__pong",
            data: { sentAt: event.data.sentAt },
            timestamp: Date.now(),
          } as RoomEvent,
        });
        return;
      }

      // Don't emit __ping or __pong to listeners
      if (event.type === "__ping" || event.type === "__pong") return;

      this.emit(event);
    });

    // Track presence
    channel.on("presence", { event: "sync" }, () => {
      const presenceState = channel.presenceState();
      const participants: Participant[] = [];
      for (const [key, presences] of Object.entries(presenceState)) {
        const p = (presences as any[])[0];
        if (p) {
          participants.push({
            id: key,
            name: p.userName,
            avatarColor: getAvatarColor(key),
            joinedAt: p.joinedAt || Date.now(),
            currentTime: p.currentTime || 0,
            playbackState: p.playbackState || "paused",
            lastPing: p.lastPing || Date.now(),
            presence: "watching",
          });
        }
      }
      this.emit({
        type: "participants",
        data: { participants },
        timestamp: Date.now(),
      });
      // Mirror the live viewer count into the rooms table so the auto-cleanup
      // edge function can detect empty rooms. Best-effort — ignore errors.
      this.updateCurrentViewersCount(participants.length);
    });

    channel.on("presence", { event: "join" }, ({ key, newPresences }: any) => {
    });

    // Subscribe to the channel
    channel.subscribe(async (status: string) => {
      if (status === "SUBSCRIBED") {
        // Track our presence FIRST — this ensures we appear in everyone's presence sync
        channel.track({
          userId: this.userId,
          userName: this.userName,
          joinedAt: Date.now(),
          currentTime: 0,
          playbackState: "paused",
          lastPing: Date.now(),
        });

        // Wait a moment for presence to propagate, then load state
        await new Promise((r) => setTimeout(r, 500));

        // Try to load room state from Supabase DB
        const dbState = await this.loadStateFromDB();
        if (dbState) {
          // Room exists in DB — initialize from it
          // DON'T include participants in the init — let presence sync handle that
          dbState.participants = [];
          this.hasReceivedInit = true;
          if (this.initTimeout) clearTimeout(this.initTimeout);
          this.emit({
            type: "init",
            data: dbState,
            timestamp: Date.now(),
          });
          // Broadcast our join so others know
          channel.send({
            type: "broadcast",
            event: "message",
            payload: {
              type: "system",
              data: { message: `${this.userName} joined the watch party`, userId: this.userId, userName: this.userName },
              timestamp: Date.now(),
            } as RoomEvent,
          });
          // Force a presence sync after a short delay to pick up existing participants
          setTimeout(() => {
            const presenceState = channel.presenceState();
            const participants: Participant[] = [];
            for (const [key, presences] of Object.entries(presenceState)) {
              const p = (presences as any[])[0];
              if (p) {
                participants.push({
                  id: key,
                  name: p.userName,
                  avatarColor: getAvatarColor(key),
                  joinedAt: p.joinedAt || Date.now(),
                  currentTime: p.currentTime || 0,
                  playbackState: p.playbackState || "paused",
                  lastPing: p.lastPing || Date.now(),
                  presence: "watching",
                });
              }
            }
            this.emit({
              type: "participants",
              data: { participants },
              timestamp: Date.now(),
            });
          }, 1500);
        } else {
          // Room not in DB — request init from existing participants via Realtime
          channel.send({
            type: "broadcast",
            event: "message",
            payload: {
              type: "__request_init",
              data: { userId: this.userId, userName: this.userName },
              timestamp: Date.now(),
            } as RoomEvent,
          });

          // If no one responds in 5s, we're the first
          this.initTimeout = setTimeout(() => {
            if (!this.hasReceivedInit) {
              this.hasReceivedInit = true;
              this.emit({
                type: "system",
                data: { message: `${this.userName} joined the watch party`, userId: this.userId, userName: this.userName },
                timestamp: Date.now(),
              });
            }
          }, 5000);
        }
      }
    });

    // Heartbeat — update presence every 10s
    this.heartbeatInterval = setInterval(() => {
      channel.track({
        userId: this.userId,
        userName: this.userName,
        joinedAt: Date.now(),
        currentTime: 0,
        playbackState: "paused",
        lastPing: Date.now(),
      });
    }, 10000);

    // Periodically save state to DB so new joiners can restore it
    this.stateSyncTimer = setInterval(() => {
      this.saveStateToDB();
    }, 15000);

    // Debounced last_active_at flush — runs every 5s, but only writes to the
    // DB when there has been broadcast activity since the last flush. This
    // keeps the rooms table "fresh" for the auto-cleanup edge function
    // without hammering Supabase on every single send().
    this.activitySyncTimer = setInterval(() => {
      if (this.pendingActivityFlush) {
        this.pendingActivityFlush = false;
        this.touchLastActiveAt();
      }
    }, 5000);
  }

  // ---- DB persistence (Supabase mode only) ----

  private async saveStateToDB(): Promise<void> {
    if (!supabase || !this.stateProvider) return;
    const state = this.stateProvider();
    if (!state) return;

    try {
      // Upsert room with current state. Also bump last_active_at so the
      // auto-cleanup edge function sees the room as recently active.
      await supabase.from("rooms").upsert({
        id: this.roomId,
        host_id: state.hostId,
        host_name: this.userName,
        title: state.title,
        video_url: state.videoState.url,
        video_type: state.videoState.type,
        host_only: state.hostOnly,
        last_active_at: new Date().toISOString(),
        // Store full state as JSON in a text column — we'll use the video_url
        // field as a simple "last updated" check. The full state sync happens
        // via Realtime broadcast; the DB is just a fallback for new joiners.
      }, { onConflict: "id" });
    } catch {
      // ignore — Realtime is the primary sync, DB is backup
    }
  }

  // Light-weight "touch" that only updates last_active_at. Called from the
  // debounced activitySyncTimer (~every 5s when there has been activity).
  private async touchLastActiveAt(): Promise<void> {
    if (!supabase) return;
    try {
      await supabase
        .from("rooms")
        .update({ last_active_at: new Date().toISOString() })
        .eq("id", this.roomId);
    } catch {
      /* ignore — last_active_at is best-effort */
    }
  }

  // Mirror the live presence count into the rooms table. Called from the
  // presence sync handler. Best-effort — ignore errors.
  private async updateCurrentViewersCount(count: number): Promise<void> {
    if (!supabase) return;
    try {
      await supabase
        .from("rooms")
        .update({ current_viewers_count: count })
        .eq("id", this.roomId);
    } catch {
      /* ignore — presence count is best-effort */
    }
  }

  private async loadStateFromDB(): Promise<InitData | null> {
    if (!supabase) return null;

    try {
      const { data, error } = await supabase
        .from("rooms")
        .select("*")
        .eq("id", this.roomId)
        .maybeSingle();

      if (error || !data) return null;

      // The room's admin (creator). Falls back to host_id for rooms created
      // before the admin_id migration. The frontend uses this to restore
      // host/admin status when the creator rejoins.
      const adminId = (data.admin_id as string | null) || (data.host_id as string) || "";

      // Build initial state from DB record. hostId is set to adminId so that
      // "isHost = (hostId === userId)" naturally returns true for the room's
      // creator on rejoin — this is the admin-persistence mechanism.
      return {
        videoState: {
          url: data.video_url || "",
          type: (data.video_type as VideoState["type"]) || "direct",
          playing: false,
          currentTime: 0,
          playbackRate: 1,
          updatedAt: 0,
          updatedBy: data.host_id || "",
        },
        participants: [],
        queue: [],
        bookmarks: [],
        subtitles: null,
        activePoll: null,
        hostId: adminId,
        hostOnly: data.host_only || false,
        isLocked: false,
        title: data.title || "Watch Party Room",
        raisedHands: {},
        comments: [],
        history: [],
        customReactions: [],
        analytics: null,
        adminId,
      };
    } catch {
      return null;
    }
  }

  // ---- Public API ----

  onEvent(cb: (event: RoomEvent) => void): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  onStateRequest(provider: () => InitData | null) {
    this.stateProvider = provider;
  }

  send(event: RoomEvent) {
    // Client-side chat rate limiter — max 10 messages per 10 seconds per user.
    // This is a courtesy limiter; real rate limiting should live in RLS
    // policies / a server-side function. The check is on `chat` events only.
    if (event.type === "chat") {
      const data = event.data as { userId?: string } | undefined;
      const senderId = (data && data.userId) || this.userId;
      const now = Date.now();
      const windowMs = RoomStore.CHAT_RATE_LIMIT_WINDOW_MS;
      const max = RoomStore.CHAT_RATE_LIMIT_MAX;

      const log = this.chatRateLog.get(senderId) || [];
      // Drop timestamps outside the rolling window.
      const recent = log.filter((ts) => now - ts < windowMs);
      if (recent.length >= max) {
        // Emit a local-only warning event so the sender sees feedback. We do
        // NOT broadcast this to other clients (note: no channel.send / bc.post).
        this.emit({
          type: "system",
          data: {
            message: "You're sending messages too quickly — slow down a moment.",
            userId: this.userId,
            userName: this.userName,
          },
          timestamp: now,
        });
        return;
      }
      recent.push(now);
      this.chatRateLog.set(senderId, recent);
    }

    this.emit(event);

    if (this.channel) {
      this.channel.send({
        type: "broadcast",
        event: "message",
        payload: event,
      });
      // Mark that there's pending activity — the debounced activitySyncTimer
      // (every ~5s) will flush last_active_at to the DB. We avoid hitting
      // Supabase on every single send().
      this.pendingActivityFlush = true;
    } else if (this.bc) {
      this.bc.postMessage(event);
    }
  }

  disconnect() {
    if (this.channel) {
      this.channel.send({
        type: "broadcast",
        event: "message",
        payload: {
          type: "__leave",
          data: { userId: this.userId, userName: this.userName },
          timestamp: Date.now(),
        } as RoomEvent,
      });
      // Save final state to DB before leaving
      this.saveStateToDB();
      this.channel.untrack();
      this.channel.unsubscribe();
    }
    if (this.bc) {
      this.bc.postMessage({
        __type: "leave",
        userId: this.userId,
        userName: this.userName,
      });
      this.bc.close();
    }
    if (this.initTimeout) clearTimeout(this.initTimeout);
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    if (this.stateSyncTimer) clearInterval(this.stateSyncTimer);
    if (this.activitySyncTimer) clearInterval(this.activitySyncTimer);
    this.listeners.clear();
  }

  private emit(event: RoomEvent) {
    this.listeners.forEach((cb) => {
      try {
        cb(event);
      } catch {
        /* listener error */
      }
    });
  }

  // ---- Static helpers ----

  static async createRoom(hostName: string, hostOnly: boolean, title: string) {
    const roomId = nanoid(10);
    const hostId = nanoid(12);

    // If Supabase is configured, persist the room to the DB
    if (isSupabaseConfigured && supabase) {
      try {
        await supabase.from("rooms").insert({
          id: roomId,
          host_id: hostId,
          host_name: hostName.trim(),
          title: (title || "Watch Party Room").trim(),
          video_url: "",
          video_type: "direct",
          host_only: hostOnly,
          // Admin persistence: store the creator's user_id so that when they
          // rejoin later (even after a refresh that clears sessionStorage) the
          // app can restore their host/admin status by comparing userId === admin_id.
          admin_id: hostId,
          // Fresh activity timestamp so the auto-cleanup function doesn't
          // immediately reap a brand-new room.
          last_active_at: new Date().toISOString(),
          current_viewers_count: 0,
          deleted_at: null,
        });
      } catch (err) {
        console.error("Failed to create room in Supabase:", err);
      }
    }

    return {
      roomId,
      hostId,
      hostName: hostName.trim(),
      title: (title || "Watch Party Room").trim(),
    };
  }

  // Returns true if the room exists and is NOT soft-deleted. Used by the
  // recent-rooms widget to detect rooms that the auto-cleanup CRON has
  // expired — in which case we show a toast and drop the entry.
  // In local mode (no Supabase) we optimistically return true; the
  // BroadcastChannel local mode is per-browser and there's nothing to
  // "expire".
  static async checkRoomExists(roomId: string): Promise<boolean> {
    if (!isSupabaseConfigured || !supabase) return true;
    try {
      const { data, error } = await supabase
        .from("rooms")
        .select("id, deleted_at")
        .eq("id", roomId)
        .maybeSingle();
      if (error) return true; // be permissive on error — don't block rejoin
      if (!data) return false; // room doesn't exist (hard-deleted by CRON)
      // Soft-deleted rooms count as "gone" — the cleanup function sets
      // deleted_at right before hard-deleting, so the user sees the toast.
      if (data.deleted_at) return false;
      return true;
    } catch {
      return true; // be permissive on network error
    }
  }

  static async loadChatHistory(roomId: string): Promise<ChatMessage[]> {
    if (isSupabaseConfigured && supabase) {
      try {
        const { data, error } = await supabase
          .from("messages")
          .select("*")
          .eq("room_id", roomId)
          .order("created_at", { ascending: true })
          .limit(200);

        if (!error && data) {
          return data.map((row: any) => ({
            id: row.id,
            userId: row.user_id,
            userName: row.user_name,
            content: row.content,
            type: row.type || "chat",
            createdAt: row.created_at,
          }));
        }
      } catch {
        // fall through to local
      }
    }

    // Local fallback
    try {
      const raw = localStorage.getItem(`syncscreen:chat:${roomId}`);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  static async saveChatMessage(roomId: string, msg: ChatMessage) {
    if (isSupabaseConfigured && supabase) {
      try {
        await supabase.from("messages").insert({
          room_id: roomId,
          user_id: msg.userId,
          user_name: msg.userName,
          content: msg.content,
          type: msg.type,
        });
        return;
      } catch {
        // fall through to local
      }
    }

    // Local fallback
    try {
      const key = `syncscreen:chat:${roomId}`;
      const raw = localStorage.getItem(key);
      const msgs: ChatMessage[] = raw ? JSON.parse(raw) : [];
      msgs.push(msg);
      localStorage.setItem(key, JSON.stringify(msgs.slice(-200)));
    } catch {
      /* ignore */
    }
  }

  static async loadComments(roomId: string): Promise<TimeComment[]> {
    if (isSupabaseConfigured && supabase) {
      try {
        const { data, error } = await supabase
          .from("time_comments")
          .select("*")
          .eq("room_id", roomId)
          .order("time", { ascending: true });

        if (!error && data) {
          return data.map((row: any) => ({
            id: row.id,
            time: row.time,
            content: row.content,
            createdBy: row.user_id,
            createdByName: row.user_name,
            createdAt: new Date(row.created_at).getTime(),
          }));
        }
      } catch {
        // fall through
      }
    }

    try {
      const raw = localStorage.getItem(`syncscreen:comments:${roomId}`);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  static async saveComment(roomId: string, comment: TimeComment) {
    if (isSupabaseConfigured && supabase) {
      try {
        await supabase.from("time_comments").upsert({
          id: comment.id,
          room_id: roomId,
          time: comment.time,
          content: comment.content,
          user_id: comment.createdBy,
          user_name: comment.createdByName,
        });
        return;
      } catch {
        // fall through
      }
    }

    try {
      const key = `syncscreen:comments:${roomId}`;
      const raw = localStorage.getItem(key);
      const comments: TimeComment[] = raw ? JSON.parse(raw) : [];
      comments.push(comment);
      localStorage.setItem(key, JSON.stringify(comments));
    } catch {
      /* ignore */
    }
  }

  static async deleteComment(roomId: string, commentId: string) {
    if (isSupabaseConfigured && supabase) {
      try {
        await supabase.from("time_comments").delete().eq("id", commentId);
        return;
      } catch {
        // fall through
      }
    }

    try {
      const key = `syncscreen:comments:${roomId}`;
      const raw = localStorage.getItem(key);
      const comments: TimeComment[] = raw ? JSON.parse(raw) : [];
      const filtered = comments.filter((c) => c.id !== commentId);
      localStorage.setItem(key, JSON.stringify(filtered));
    } catch {
      /* ignore */
    }
  }

  static async fetchPreview(url: string): Promise<PreviewData> {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return { title: null, author: null, thumbnail: null, provider: "Direct", url };
    }

    const host = parsed.hostname.toLowerCase();
    const isYoutube = host.includes("youtube.com") || host.includes("youtu.be");
    const isVimeo = host.includes("vimeo.com");

    let oembedUrl: string | null = null;
    if (isYoutube) oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
    else if (isVimeo) oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`;

    if (oembedUrl) {
      try {
        const res = await fetch(oembedUrl, { signal: AbortSignal.timeout(4000) });
        if (res.ok) {
          const data = await res.json();
          return {
            title: data.title || null,
            author: data.author_name || null,
            thumbnail: data.thumbnail_url || null,
            provider: data.provider_name || (isYoutube ? "YouTube" : "Vimeo"),
            url,
          };
        }
      } catch {
        /* network blocked — fall through */
      }
    }

    let provider = "Direct";
    if (isYoutube) provider = "YouTube";
    else if (isVimeo) provider = "Vimeo";
    else if (host.includes("twitch.tv")) provider = "Twitch";
    else if (url.toLowerCase().includes(".m3u8")) provider = "HLS";

    return { title: null, author: null, thumbnail: null, provider, url };
  }

  static async uploadReaction(roomId: string, file: File): Promise<{ url: string }> {
    if (isSupabaseConfigured && supabase) {
      const ext = file.name.split(".").pop() || "png";
      const filename = `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { data, error } = await supabase.storage
        .from("reactions")
        .upload(`${roomId}/${filename}`, file);
      if (!error && data) {
        const { data: urlData } = supabase.storage
          .from("reactions")
          .getPublicUrl(`${roomId}/${filename}`);
        return { url: urlData.publicUrl };
      }
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ url: String(reader.result || "") });
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }
}
